import type { Id } from "@convex/_generated/dataModel";
import { formatDistanceToNow } from "@plasma/utils/time";
import { useAction, useQuery } from "convex/react";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Edit2,
  Info,
  RefreshCw,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  locationsGetAllMine,
  stripeTaxGetStaleLocations,
  stripeTaxManuallyUpdateTaxRate,
  stripeTaxManualSyncTaxRate,
} from "~/api-refs";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { logger } from "~/lib/logger";

export function TaxRatesSettings() {
  const locations = useQuery(locationsGetAllMine, {});
  const staleLocations = useQuery(stripeTaxGetStaleLocations);
  const [syncingLocationId, setSyncingLocationId] =
    useState<Id<"locations"> | null>(null);
  const [editingLocationId, setEditingLocationId] =
    useState<Id<"locations"> | null>(null);
  const [editValue, setEditValue] = useState("");

  const syncTaxRate = useAction(stripeTaxManualSyncTaxRate);
  const updateTaxRate = useAction(stripeTaxManuallyUpdateTaxRate);

  const handleSyncTaxRate = async (locationId: Id<"locations">) => {
    try {
      setSyncingLocationId(locationId);
      await syncTaxRate({ locationId });
      // Success - query will auto-refresh
    } catch (error) {
      logger.exception("tax_rate_sync_failed", error);
      toast.error("Failed to sync tax rate. Please try again.");
    } finally {
      setSyncingLocationId(null);
    }
  };

  const handleStartEdit = (
    locationId: Id<"locations">,
    currentRate: number | undefined
  ) => {
    setEditingLocationId(locationId);
    setEditValue(currentRate?.toString() ?? "");
  };

  const handleCancelEdit = () => {
    setEditingLocationId(null);
    setEditValue("");
  };

  const handleSaveEdit = async (locationId: Id<"locations">) => {
    const rate = Number.parseFloat(editValue);

    if (Number.isNaN(rate) || rate < 0 || rate > 20) {
      toast.error("Please enter a valid tax rate between 0% and 20%");
      return;
    }

    try {
      await updateTaxRate({ locationId, taxPercentage: rate });
      setEditingLocationId(null);
      setEditValue("");
    } catch (error) {
      logger.exception("tax_rate_update_failed", error);
      toast.error("Failed to update tax rate. Please try again.");
    }
  };

  if (!locations) {
    return <LoadingPanel message="Loading tax rates..." />;
  }

  const staleCount = staleLocations?.length ?? 0;

  return (
    <div className="space-y-6">
      {/* Important Disclaimer */}
      <Alert>
        <AlertCircle className="size-4" />
        <AlertTitle>Tax Compliance Notice</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            <strong>
              You are responsible for ensuring accurate sales tax rates.
            </strong>{" "}
            Plasma automatically updates tax rates monthly using Stripe Tax, but
            you should verify rates are correct for your jurisdiction.
          </p>
          <p className="text-muted-foreground text-sm">
            Tax rates are automatically synced on the 1st of every month. You
            can also manually refresh rates at any time using the "Refresh Rate"
            button below.
          </p>
        </AlertDescription>
      </Alert>

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="size-5" />
            How Tax Rates Work
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 text-sm">
            <div>
              <h4 className="mb-1 font-medium">1. Initial Setup</h4>
              <p className="text-muted-foreground">
                When you create a location, we use Stripe Tax to lookup the
                correct tax rate based on your location's address.
              </p>
            </div>
            <div>
              <h4 className="mb-1 font-medium">2. Automatic Monthly Updates</h4>
              <p className="text-muted-foreground">
                On the 1st of every month, we automatically refresh all tax
                rates to ensure they stay current. This catches most tax rate
                changes (which typically happen January 1st or July 1st).
              </p>
            </div>
            <div>
              <h4 className="mb-1 font-medium">3. Manual Refresh</h4>
              <p className="text-muted-foreground">
                If you hear about a tax rate change, you can manually refresh
                the rate at any time using the "Refresh Rate" button below.
              </p>
            </div>
            <div>
              <h4 className="mb-1 font-medium">4. Your Responsibility</h4>
              <p className="text-muted-foreground">
                <strong className="text-foreground">
                  Plasma is not responsible for tax compliance.
                </strong>{" "}
                You are ultimately responsible for charging the correct tax
                rate. Subscribe to your state tax authority notifications and
                manually refresh rates when needed.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Staleness Warning */}
      {staleCount > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Tax Rates Need Review</AlertTitle>
          <AlertDescription>
            {staleCount} location{staleCount > 1 ? "s have" : " has"} tax rates
            that haven't been verified in over 90 days. Please review and
            refresh them below.
          </AlertDescription>
        </Alert>
      )}

      {/* Location Tax Rates */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Location Tax Rates</h3>
        <div className="grid gap-4">
          {locations?.map((location) => {
            const locationWithTax = location as typeof location & {
              taxPercentage?: number;
              taxLastUpdated?: number;
              taxUpdateSource?: string;
            };
            const isStale = staleLocations?.some((r) => r._id === location.id);
            const isSyncing = syncingLocationId === location.id;
            const isEditing = editingLocationId === location.id;

            return (
              <Card key={location.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{location.name}</h4>
                        {isStale && (
                          <Badge className="text-xs" variant="destructive">
                            Needs Update
                          </Badge>
                        )}
                        {!isStale && locationWithTax.taxPercentage && (
                          <Badge className="text-xs" variant="outline">
                            <CheckCircle2 className="mr-1 size-3" />
                            Up to date
                          </Badge>
                        )}
                      </div>

                      {isEditing ? (
                        <div className="max-w-xs space-y-2">
                          <div className="flex items-center gap-2">
                            <Input
                              autoFocus
                              className="w-32"
                              max="20"
                              min="0"
                              onChange={(e) => setEditValue(e.target.value)}
                              placeholder="8.875"
                              step="0.001"
                              type="number"
                              value={editValue}
                            />
                            <span className="text-muted-foreground">%</span>
                          </div>
                          <p className="text-muted-foreground text-xs">
                            Enter tax rate between 0% and 20%
                          </p>
                          <Alert className="text-xs">
                            <AlertCircle className="size-3" />
                            <AlertDescription>
                              <strong>You are responsible</strong> for ensuring
                              this rate is accurate for your jurisdiction.
                            </AlertDescription>
                          </Alert>
                        </div>
                      ) : (
                        (() => {
                          if (locationWithTax.taxPercentage) {
                            return (
                              <div className="space-y-1">
                                <p className="text-2xl font-bold">
                                  {locationWithTax.taxPercentage}%
                                </p>
                                <div className="text-muted-foreground space-y-0.5 text-sm">
                                  {locationWithTax.taxLastUpdated && (
                                    <p>
                                      Last updated:{" "}
                                      {formatDistanceToNow(
                                        locationWithTax.taxLastUpdated
                                      )}
                                    </p>
                                  )}
                                  {locationWithTax.taxUpdateSource && (
                                    <p className="capitalize">
                                      Source:{" "}
                                      {locationWithTax.taxUpdateSource.replace(
                                        "_",
                                        " "
                                      )}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div className="space-y-1">
                              <p className="text-muted-foreground">
                                No tax rate configured
                              </p>
                              <p className="text-muted-foreground text-sm">
                                Click "Lookup Rate" or "Edit Manually" to set
                                the tax rate
                              </p>
                            </div>
                          );
                        })()
                      )}
                    </div>

                    <div className="flex gap-2">
                      {isEditing ? (
                        <>
                          <Button
                            onClick={handleCancelEdit}
                            size="sm"
                            variant="ghost"
                          >
                            <X className="mr-2 size-4" />
                            Cancel
                          </Button>
                          <Button
                            onClick={() => handleSaveEdit(location.id)}
                            size="sm"
                            variant="default"
                          >
                            <Check className="mr-2 size-4" />
                            Save
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() =>
                              handleStartEdit(
                                location.id,
                                locationWithTax.taxPercentage
                              )
                            }
                            size="sm"
                            variant="outline"
                          >
                            <Edit2 className="mr-2 size-4" />
                            Edit Manually
                          </Button>
                          <Button
                            disabled={isSyncing}
                            onClick={() => handleSyncTaxRate(location.id)}
                            size="sm"
                            variant="outline"
                          >
                            {isSyncing ? (
                              <>
                                <RefreshCw className="mr-2 size-4 animate-spin" />
                                Looking up...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="mr-2 size-4" />
                                Lookup Rate
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Additional Resources */}
      <Card>
        <CardHeader>
          <CardTitle>Tax Authority Resources</CardTitle>
          <CardDescription>
            Subscribe to these resources to stay informed about tax rate changes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li>
              <a
                className="text-primary hover:underline"
                href="https://www.salestaxinstitute.com/resources/rate-changes"
                rel="noopener noreferrer"
                target="_blank"
              >
                Sales Tax Institute - Rate Changes Calendar
              </a>
            </li>
            <li>
              <a
                className="text-primary hover:underline"
                href="https://www.cdtfa.ca.gov/services/"
                rel="noopener noreferrer"
                target="_blank"
              >
                California Department of Tax and Fee Administration (if
                applicable)
              </a>
            </li>
            <li className="text-muted-foreground">
              Subscribe to your state's tax authority email list for
              notifications about rate changes
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
